import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Boxes, Search, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";

export default function SpareParts() {
  const [parts, setParts] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/content/spareparts").then((r) => setParts(r.data)).catch(() => {});
    api.get("/content/approvals").then((r) => setApprovals(r.data)).catch(() => {});
  }, []);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return parts;
    return parts.filter((p) => ["product", "model", "part_name", "part_code", "compatible_models"]
      .some((f) => (p[f] || "").toString().toLowerCase().includes(t)));
  }, [q, parts]);

  return (
    <div className="space-y-6" data-testid="spare-parts-page">
      <PageHeader overline="Parts" title="Spare Parts & Replacement Process" icon={Boxes}
        color="text-cyan-600 bg-cyan-50" desc="Search spare parts. Troubleshooting is required before any replacement." />

      <div className="relative max-w-2xl">
        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="parts-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by part name, code or model"
          className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-cyan-500" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {results.map((p) => (
          <Card key={p.id} className="p-5" data-testid={`part-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-bold text-slate-900">{p.part_name}</h3>
                <div className="text-sm text-slate-500">{p.product} · {p.model}</div>
              </div>
              <Badge variant="outline" className="font-mono">{p.part_code}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div><span className="text-xs uppercase tracking-wider font-bold text-slate-400 block">Compatible</span>{p.compatible_models}</div>
              <div><span className="text-xs uppercase tracking-wider font-bold text-slate-400 block">Approval required</span>
                <Badge className={p.approval_required === "Yes" ? "bg-red-500" : "bg-emerald-500"}>{p.approval_required}</Badge>
              </div>
            </div>
            {p.install_instructions && <p className="text-sm text-slate-600 mt-3"><span className="font-semibold">Install: </span>{p.install_instructions}</p>}
            {p.notes && <p className="text-xs text-slate-500 mt-2">{p.notes}</p>}
          </Card>
        ))}
      </div>

      {/* Approval guide */}
      <Card className="p-6" data-testid="approval-guide">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="w-5 h-5 text-mlily-blue" />
          <h3 className="font-heading font-bold text-slate-900">Approval Guide</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {approvals.map((a) => (
            <div key={a.id} className="py-3 grid sm:grid-cols-3 gap-2 items-center">
              <div className="font-medium text-slate-900">{a.outcome}</div>
              <div className="text-sm"><Badge className="bg-mlily-blue">{a.approver}</Badge></div>
              <div className="text-sm text-slate-500">{a.notes}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
